"use client";

import { Clock } from "lucide-react";
import StepHeading from "@/app/components/molecules/StepHeading";
import FormCard from "@/app/components/atoms/FormCard";
import Button from "@/app/components/atoms/Button";
import ConfirmedEmailCard from "@/app/components/atoms/ConfirmedEmailCard";

interface RegisterAltruPendingStepProps {
  /** Called when the user clicks the retry button. */
  onRetry: () => void;
  /** The email address being registered. */
  email: string;
  /** Called when the user wants to go back and change their email. */
  onEditEmail: () => void;
  /** Whether a retry is in progress. */
  loading?: boolean;
  /** Error message to display after a failed retry. */
  error?: string | null;
}

/**
 * @component RegisterAltruPendingStep
 * @description Shown when the user returns from creating a ticketing account
 * but the new constituent record is not yet searchable in Altru. Instructs
 * the user to wait a moment and retry. A second retry failure routes to
 * contact support.
 */
export default function RegisterAltruPendingStep({
  onRetry,
  email,
  onEditEmail,
  loading,
  error,
}: RegisterAltruPendingStepProps) {
  return (
    <div className="flex flex-col gap-8">
      <StepHeading id="register-altru-pending-header" title="Create an account" />

      <div className="w-full max-w-md m-auto flex flex-col gap-4">
        <ConfirmedEmailCard email={email} onEdit={onEditEmail} />

        <FormCard>
          <div className="flex justify-center">
            <Clock className="w-14 h-14 text-charcoal" aria-hidden />
          </div>

          <div className="flex flex-col gap-3 pb-6 border-b border-card-stroke">
            <p className="text-title text-charcoal">Your account is still processing</p>
            <p className="text-body-base text-charcoal">
              Your ticketing account may take a moment to become available.
              Please wait a few seconds and try again.
            </p>
          </div>

          {error && (
            <p role="alert" className="text-body-base text-destructive text-center">
              {error}
            </p>
          )}

          <Button
            variant="primary"
            size="full"
            onClick={onRetry}
            loading={loading}
          >
            Try again
          </Button>
        </FormCard>
      </div>
    </div>
  );
}
